import cx from 'classnames'

import Styles from './PostPreview.module.scss'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { TwitterBrand } from 'blaze-ui/icons/35/TwitterBrand'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { FacebookBrand } from 'blaze-ui/icons/35/FacebookBrand'
import { LinkedInBrand } from 'blaze-ui/icons/35/LinkedInBrand'
import { InstagramBrand } from 'blaze-ui/icons/35/InstagramBrand'
import { useShowDefaultProfileIcon } from 'screens/blaze/Home/components/Catalogue/Integrations/components/Integrate/components/ExistingIntegration/useShowDefaultProfileIcon'
import { UserProfileCircle } from 'blaze-ui/icons/32/UserProfileCircle'

export const PostPreview = ({ post, externalIntegrationId }: { post: any; externalIntegrationId: number }) => {
  const { data } = useExternalIntegrations({
    staleTime: 1000 * 60 * 5,
  })
  const integration = data?.external_integrations?.find(
    (externalIntegration) => externalIntegration.id === externalIntegrationId,
  )
  const { showProfile } = useShowDefaultProfileIcon(integration?.picture)

  if (!integration) return null

  const providerIcon: { [key: string]: JSX.Element } = {
    instagram: <InstagramBrand />,
    linkedin: <LinkedInBrand />,
    x: <TwitterBrand />,
    facebook: <FacebookBrand />,
  }

  if (post.media_url) {
    return (
      <div className={Styles.preview}>
        <img data-testid={`${post.id}-image`} src={post.media_url} alt={post.image_description} />
        <div className={Styles.logo}>{providerIcon[integration.provider]}</div>
      </div>
    )
  }

  const isX = integration.provider === ExternalIntegrationEnum.X

  return (
    <div className={cx(Styles.textContainer, Styles[integration.provider])}>
      <div className={Styles.header}>
        <div className={Styles.avatar}>
          {showProfile || !integration?.picture ? (
            <UserProfileCircle size={16} />
          ) : (
            <img src={integration.picture} alt={`${integration.provider} account`} />
          )}
        </div>
        <div className={cx(Styles.accountInfo, Styles[integration.provider])}>
          <p className={cx(Styles.name, Styles[integration.provider])}>{integration.name}</p>
          <div className={cx(Styles.other, Styles[integration.provider])}>
            <p>1m • Edited {isX ? '' : '•'}</p>

            {!isX && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="9"
                viewBox="0 0 8 9"
                fill="none"
                data-testid="earth-icon"
              >
                <g>
                  <path
                    d="M4.01616 1.37354C3.35695 1.37354 2.71254 1.56902 2.16442 1.93526C1.6163 2.3015 1.18909 2.82205 0.936821 3.43109C0.68455 4.04012 0.618544 4.71029 0.747151 5.35684C0.875757 6.00339 1.1932 6.59728 1.65934 7.06342C2.12547 7.52956 2.71937 7.847 3.36592 7.97561C4.01247 8.10421 4.68263 8.03821 5.29167 7.78594C5.90071 7.53366 6.42126 7.10646 6.7875 6.55834C7.15374 6.01022 7.34922 5.36581 7.34922 4.70659C7.34922 3.82261 6.99806 2.97483 6.37299 2.34976C5.74792 1.7247 4.90015 1.37354 4.01616 1.37354ZM1.63541 4.70659C1.63541 4.19147 1.80249 3.69024 2.11156 3.27814L2.37344 3.54002C2.44121 3.60645 2.49507 3.68572 2.53186 3.7732C2.56865 3.86067 2.58763 3.95461 2.58771 4.0495V4.55899C2.58779 4.65367 2.62547 4.74444 2.69246 4.81135L2.95911 5.07799C3.02602 5.14498 3.11679 5.18266 3.21147 5.18274H3.54001V5.51129C3.5401 5.60597 3.57778 5.69674 3.64477 5.76365L3.91141 6.03029C3.9784 6.0972 4.01608 6.18797 4.01616 6.28265V7.08735C3.38475 7.08735 2.77919 6.83652 2.33272 6.39004C1.88624 5.94356 1.63541 5.33801 1.63541 4.70659ZM4.60659 7.0064L5.55889 5.83507C5.60151 5.78222 5.62817 5.71831 5.63576 5.65085C5.64335 5.58338 5.63155 5.51515 5.60175 5.45415L5.32558 4.90181C5.2925 4.83721 5.24075 4.78406 5.17707 4.74924C5.11338 4.71443 5.0407 4.69957 4.96847 4.70659H3.54001V4.61612C3.54047 4.57162 3.55314 4.5281 3.57666 4.49031C3.60017 4.45252 3.63361 4.42192 3.67334 4.40186L3.85427 4.31139C3.90431 4.2855 3.95983 4.27199 4.01616 4.27199C4.0725 4.27199 4.12802 4.2855 4.17806 4.31139L4.49232 4.46852L4.67325 4.13521C4.71114 4.06502 4.73079 3.98643 4.73039 3.90666V3.50193C4.73069 3.40821 4.7664 3.31806 4.83038 3.24957L5.33986 2.73057C5.72206 2.98618 6.02205 3.347 6.20361 3.76944C6.38516 4.19187 6.44051 4.65784 6.36296 5.11104C6.28541 5.56425 6.07829 5.98531 5.76661 6.32334C5.45493 6.66138 5.05203 6.90192 4.60659 7.01592V7.0064Z"
                    fill="black"
                    fillOpacity="0.4"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_2970_37384">
                    <rect width="7.61842" height="7.61842" fill="white" transform="translate(0.20697 0.897461)" />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
        </div>
      </div>
      <div className={cx(Styles.caption, Styles[integration.provider])}>{post.caption}</div>
      <div className={Styles.logo}>{providerIcon[integration.provider]}</div>
    </div>
  )
}
